import React, { useState, useEffect } from "react";
import { ShieldCheck, Loader2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

/**
 * ConfidentialityConfirm — small acknowledgement modal shown before an
 * inquiry is actually dispatched. Forces the prospect to opt-in to the
 * confidentiality clause, so it is on record for both sides.
 */
const ConfidentialityConfirm = ({
  open,
  onOpenChange,
  onConfirm,
  loading = false,
}) => {
  const [agreed, setAgreed] = useState(false);

  // Reset state every time the modal opens
  useEffect(() => {
    if (open) setAgreed(false);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0a1428]/95 border border-white/10 backdrop-blur-xl text-zinc-100 max-w-md p-0 overflow-hidden"
        data-testid="confidentiality-confirm"
      >
        <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-purple-500/60 to-transparent" />
        <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-purple-700/30 blur-3xl pointer-events-none" />

        <div className="relative p-7 sm:p-8">
          <DialogHeader className="text-left">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl border border-purple-500/30 bg-purple-600/10 flex items-center justify-center text-purple-300">
                <ShieldCheck size={18} strokeWidth={1.5} />
              </div>
              <div className="text-[10px] tracking-[0.45em] uppercase text-purple-300">
                Confidentiality Acknowledgement
              </div>
            </div>
            <DialogTitle className="font-display text-2xl text-zinc-100 leading-tight">
              Before we proceed.
            </DialogTitle>
            <DialogDescription className="text-zinc-300 text-sm mt-2.5 leading-relaxed">
              Anything you share with Ah-Jin — conversations, documents,
              strategies and any material exchanged — remains strictly between
              us. It may not be disclosed, shared or reproduced with any third
              party without our prior written authorisation. The same applies
              to information you receive from us during any consultation or
              engagement.
            </DialogDescription>
          </DialogHeader>

          <label
            htmlFor="confidentiality-agree"
            className="mt-6 flex items-start gap-3 cursor-pointer select-none rounded-lg border border-white/10 bg-white/[0.02] hover:border-purple-500/30 transition-colors px-4 py-3.5"
          >
            <input
              id="confidentiality-agree"
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-white/20 bg-black/40 text-purple-500 focus:ring-purple-500 focus:ring-offset-0 cursor-pointer accent-purple-500"
              data-testid="confidentiality-checkbox"
            />
            <span className="text-xs text-zinc-200 leading-relaxed">
              I acknowledge that the information exchanged is confidential and
              will not be shared with any third party without Ah-Jin's
              prior written authorisation.
            </span>
          </label>

          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              data-testid="confidentiality-cancel-btn"
              className="text-[11px] uppercase tracking-[0.32em] text-zinc-300 hover:text-zinc-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!agreed || loading}
              data-testid="confidentiality-confirm-btn"
              className="group inline-flex items-center gap-2.5 rounded-full px-6 py-3 text-[11px] uppercase tracking-[0.32em] bg-purple-600 hover:bg-purple-500 text-white transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-[0_0_50px_-10px_rgba(147,51,234,0.9)]"
            >
              {loading ? (
                <>
                  <Loader2 size={13} className="animate-spin" /> Sending
                </>
              ) : (
                <>
                  Confirm & Send
                  <Send size={13} strokeWidth={1.6} className="group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfidentialityConfirm;
